import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { FaBell, FaBuilding, FaUsers, FaUser, FaInfoCircle } from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './CreateNotice.css';

const CreateNotice = () => {
    const navigate = useNavigate();
    const baseUrl = "https://api.burrayhr.com";
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        recipient_type: 'all',
        department: null,
        recipients: []
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (formData.recipient_type === 'department') {
            fetchDepartments();
        } else if (formData.recipient_type === 'individual') {
            fetchEmployees();
        }
    }, [formData.recipient_type]);

    const fetchDepartments = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/departments/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const departmentOptions = response.data.map(dept => ({
                value: dept.id,
                label: dept.name
            }));
            setDepartments(departmentOptions);
        } catch (error) {
            console.error('Error fetching departments:', error);
        }
    };

    const fetchEmployees = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/employees/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const employeeOptions = response.data.map(emp => ({
                value: emp.id,
                label: `${emp.first_name} ${emp.last_name}`
            }));
            setEmployees(employeeOptions);
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            // Reset recipients when changing recipient type
            ...(name === 'recipient_type' && { recipients: [], department: null })
        }));
        // Clear errors when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSelectChange = (selectedOption, { name }) => {
        setFormData(prev => ({
            ...prev,
            [name]: selectedOption
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.content.trim()) newErrors.content = 'Content is required';
        if (formData.recipient_type === 'department' && !formData.department) {
            newErrors.department = 'Please select a department';
        }
        if (formData.recipient_type === 'individual' && (!formData.recipients || formData.recipients.length === 0)) {
            newErrors.recipients = 'Please select at least one employee';
        }
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            
            // Create the base data
            const submitData = {
                title: formData.title,
                content: formData.content,
                recipient_type: formData.recipient_type
            };

            // Add department_id or recipients based on recipient type
            if (formData.recipient_type === 'department' && formData.department) {
                submitData.department = formData.department.value;
            } else if (formData.recipient_type === 'individual' && formData.recipients) {
                // Extract array of recipient IDs
                submitData.recipients = formData.recipients.map(recipient => recipient.value);
            }

            await axios.post(`${baseUrl}/api/notices/`, submitData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Notice created successfully',
                confirmButtonColor: '#1e3a8a'
            });

            navigate('/admin-manager/notices');
        } catch (error) {
            console.log(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to create notice',
                confirmButtonColor: '#1e3a8a'
            });
        } finally {
            setLoading(false);
        }
    };

  return (
    <div>
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="create-notice-container">
                    <div className="page-header">
                        <div className="header-content">
                            <h1>
                                <FaBell />
                                Create Notice
                            </h1>
                            <p className="header-description">
                                Create and send a new notice to employees
                            </p>
                        </div>
                    </div>

                    <div className="notice-form-container">
                        <form onSubmit={handleSubmit} className="notice-form">
                            <div className="form-group">
                                <label htmlFor="title">
                                    <FaBell className="field-icon" />
                                    Title <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    className={errors.title ? 'error' : ''}
                                    placeholder="Enter notice title"
                                />
                                {errors.title && (
                                    <div className="error-message">
                                        <FaInfoCircle /> {errors.title}
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="content">
                                    <FaBell className="field-icon" />
                                    Message <span className="required">*</span>
                                </label>
                                <textarea
                                    id="content"
                                    name="content"
                                    value={formData.content}
                                    onChange={handleChange}
                                    className={errors.content ? 'error' : ''}
                                    placeholder="Enter notice message"
                                    rows={6}
                                />
                                {errors.content && (
                                    <div className="error-message">
                                        <FaInfoCircle /> {errors.content}
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="recipient_type">
                                    <FaUsers className="field-icon" />
                                    Recipient Type <span className="required">*</span>
                                </label>
                                <select
                                    id="recipient_type"
                                    name="recipient_type"
                                    value={formData.recipient_type}
                                    onChange={handleChange}
                                >
                                    <option value="all">All Employees</option>
                                    <option value="department">Specific Department</option>
                                    <option value="individual">Individual Employees</option>
                                </select>
                            </div>

                            {formData.recipient_type === 'department' && (
                                <div className="form-group">
                                    <label>
                                        <FaBuilding className="field-icon" />
                                        Select Department <span className="required">*</span>
                                    </label>
                                    <Select
                                        name="department"
                                        value={formData.department}
                                        onChange={(option) => handleSelectChange(option, { name: 'department' })}
                                        options={departments}
                                        className={`react-select-container ${errors.department ? 'error' : ''}`}
                                        placeholder="Select a department"
                                    />
                                    {errors.department && (
                                        <div className="error-message">
                                            <FaInfoCircle /> {errors.department}
                                        </div>
                                    )}
                                </div>
                            )}

                            {formData.recipient_type === 'individual' && (
                                <div className="form-group">
                                    <label>
                                        <FaUser className="field-icon" />
                                        Select Employees <span className="required">*</span>
                                    </label>
                                    <Select
                                        isMulti
                                        name="recipients"
                                        value={formData.recipients}
                                        onChange={(options) => handleSelectChange(options, { name: 'recipients' })}
                                        options={employees}
                                        className={`react-select-container ${errors.recipients ? 'error' : ''}`}
                                        placeholder="Select employees"
                                    />
                                    {errors.recipients && (
                                        <div className="error-message">
                                            <FaInfoCircle /> {errors.recipients}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="cancel-button"
                                    onClick={() => navigate('/admin-manager/notices')}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="submit-button"
                                    disabled={loading}
                                >
                                    {loading ? 'Creating...' : 'Create Notice'}
                                </button>
                            </div>
                        </form>
            </div>
          </div>
        </div>
    </div>
    );
};

export default CreateNotice;